<template>
    <Component :is="sliceVariationComponent" v-bind="props" />
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {getSliceComponentProps} from '@prismicio/vue';
    import type {FaqSlice} from '@prismicio-types';
    import FaqDefault from './variations/FaqDefault.vue';
    import FaqWithImage from './variations/FaqWithImage.vue';
    import {getSliceVariationComponent} from '../../utils/sliceVariations';

    const props = defineProps(getSliceComponentProps<FaqSlice>());

    const variationComponents = {
        default: FaqDefault,
        faqWithImage: FaqWithImage,
    };

    const sliceVariationComponent = computed(() => getSliceVariationComponent(props.slice, variationComponents));
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .slice-faq {
        &--hide-in-mobile {
            display: none;

            @include mq.screen_sm(min) {
                display: block;
            }
        }

        &__link-container {
            grid-column-end: span 2;
            display: flex;
            justify-content: center;
        }

        &__link {
            color: var(--joy-color-secondary-30);
            font-weight: var(--joy-font-weight-bold);

            &:hover {
                text-decoration: underline;
            }
        }

        &__items {
            margin-top: var(--joy-core-spacing-8);
            width: 100%;
            display: flex;
            flex-direction: column;
            text-align: left;

            &--full-width {
                grid-column-end: span 2;
            }

            @include mq.screen_sm(max) {
                margin-top: var(--joy-core-spacing-6);
            }
        }

        &__item {
            border-bottom: 1px solid var(--joy-color-neutral-30);

            &:last-child {
                border: none;
            }

            .faq-collapsible__header {
                padding: var(--joy-core-spacing-6) 0;
            }
        }

        &__question-title {
            margin-right: var(--joy-core-spacing-4);

            h2,
            h3 {
                font-family: var(--joy-font-family-title);
                color: var(--joy-color-neutral-40);
                font-size: var(--joy-font-size-primary-600);
                text-align: left;
            }
        }

        &__answer {
            margin-bottom: var(--joy-core-spacing-7);
            line-height: var(--joy-line-height-large);
            font-size: var(--joy-font-size-primary-400);
            color: var(--joy-color-neutral-50);

            h3 {
                font-size: var(--joy-font-size-primary-400);
            }

            a,
            a:visited {
                color: var(--joy-color-secondary-30);
                font-weight: var(--joy-font-weight-bold);
            }

            a:hover {
                text-decoration: underline;
            }

            p:not(:first-child) {
                margin-top: var(--joy-core-spacing-4);
            }

            ul,
            ol {
                margin: var(--joy-core-spacing-8) var(--joy-core-spacing-4);
                margin-right: 0;
            }
        }
    }
</style>
